import React from "react";
import { motion } from "framer-motion";
import { Star, Quote, ExternalLink } from "lucide-react";
import { REVIEWS, GOOGLE_REVIEW_URL } from "@/data/site";

const GoogleG = ({ className }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Stars = ({ n = 5, className = "h-4 w-4" }) => (
  <div className="flex gap-0.5">
    {[...Array(n)].map((_, i) => (
      <Star key={i} className={`${className} fill-[#FBBC05] text-[#FBBC05]`} />
    ))}
  </div>
);

const card = {
  hidden: { y: 30, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const ReviewCard = ({ review }) => (
  <motion.div
    variants={card}
    className="flex w-[78%] shrink-0 snap-start flex-col rounded-3xl border border-black/5 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:w-auto sm:p-6"
    data-testid="review-card"
  >
    <div className="flex items-center justify-between">
      <Stars className="h-4 w-4" />
      <GoogleG className="h-5 w-5" />
    </div>
    <Quote className="mt-4 h-6 w-6 text-[var(--kilic-blue)]/15" />
    <p className="mt-1 flex-1 text-sm leading-relaxed text-neutral-700">
      "{review.text}"
    </p>
    <div className="mt-5 flex items-center gap-3 border-t border-black/5 pt-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[var(--kilic-blue)] to-[var(--kilic-green)] font-display font-bold text-white">
        {review.name.charAt(0)}
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-neutral-900">{review.name}</div>
        <div className="text-xs text-neutral-500">
          {review.city} · {review.date}
        </div>
      </div>
    </div>
  </motion.div>
);

export const Reviews = () => {
  return (
    <section id="bewertungen" className="relative bg-[#fafafa] py-20 md:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[var(--kilic-green-dark)]">
              Referenzen
            </span>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
              Das sagen unsere Kunden
            </h2>

            <div className="mt-8 rounded-3xl border border-black/5 bg-white p-7 shadow-sm">
              <div className="flex items-center gap-3">
                <GoogleG className="h-9 w-9" />
                <div>
                  <div className="font-display text-sm font-bold text-neutral-900">
                    Google Bewertungen
                  </div>
                  <div className="text-xs text-neutral-500">Verifizierte Rezensionen</div>
                </div>
              </div>
              <div className="mt-5 flex items-end gap-3">
                <div className="font-display text-5xl font-extrabold text-neutral-900">5,0</div>
                <div className="pb-1">
                  <Stars className="h-5 w-5" />
                  <div className="mt-1 text-xs text-neutral-500">
                    Basierend auf Kundenbewertungen
                  </div>
                </div>
              </div>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
              >
                Auf Google ansehen
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <p className="mt-4 flex items-center gap-2 text-xs font-semibold text-neutral-400 sm:hidden">
              <span aria-hidden>←</span> Zum Wischen für weitere Bewertungen{" "}
              <span aria-hidden>→</span>
            </p>
          </motion.div>

          {/* Grid / mobile swipe */}
          <motion.div
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-pl-1 pb-3 pl-1 pr-6 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:p-0 lg:col-span-8"
            style={{ scrollbarWidth: "none" }}
            data-testid="reviews-track"
          >
            {REVIEWS.map((r, i) => (
              <ReviewCard key={i} review={r} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
